import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarIcon, CheckCircle2, Unlink, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { adminGoogleStatus, adminGoogleConnect, adminGoogleDisconnect, adminGooglePollNow } from '@/lib/api';

export default function GoogleCalendarConnect() {
  const [status, setStatus] = useState({ connected: false, email: null, configured: true });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const res = await adminGoogleStatus();
      setStatus(res.data);
    } catch (_e) { /* ignore */ }
  };

  useEffect(() => {
    refresh();
    // If we just came back from the OAuth callback, show a toast
    const params = new URLSearchParams(window.location.search);
    if (params.get('gcal_connected') === '1') {
      toast.success('Google Calendar connected!');
      window.history.replaceState({}, '', '/admin/dashboard');
      refresh();
    } else if (params.get('gcal_error')) {
      toast.error(`Google connect failed: ${params.get('gcal_error')}`);
      window.history.replaceState({}, '', '/admin/dashboard');
    }
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const res = await adminGoogleConnect();
      window.location.href = res.data.authorization_url;
    } catch (e) {
      toast.error('Could not start Google OAuth');
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Google Calendar? New bookings will stop syncing until you reconnect.')) return;
    setBusy(true);
    try {
      await adminGoogleDisconnect();
      toast.success('Google Calendar disconnected');
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const pollNow = async () => {
    setBusy(true);
    try {
      const res = await adminGooglePollNow();
      const d = res.data || {};
      toast.success(
        `Synced — ${d.imported || 0} imported · ${d.updated || 0} updated · ${d.removed || 0} removed`
      );
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Sync failed';
      const expired = /expired|revoked|reconnect|invalid_grant/i.test(String(detail));
      toast.error(detail, {
        duration: 8000,
        action: expired
          ? { label: 'Reconnect', onClick: connect }
          : undefined,
      });
      // Pull latest status in case we became disconnected
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  if (!status.configured) {
    return (
      <div className="text-xs text-zinc-500 italic">Google Calendar not configured on server</div>
    );
  }

  if (!status.connected) {
    return (
      <Button
        onClick={connect}
        disabled={busy}
        className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
        size="sm"
        data-testid="gcal-connect-btn"
      >
        <CalendarIcon className="w-4 h-4 mr-1.5" />
        {busy ? 'Opening…' : 'Connect Google Calendar'}
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="flex items-center gap-1.5 text-xs text-green-500 uppercase tracking-wider" data-testid="gcal-status-connected">
        <CheckCircle2 className="w-4 h-4" />
        <span className="hidden lg:inline">Google:</span>
        <span className="font-mono normal-case text-zinc-300">{status.email}</span>
      </span>
      <Button
        variant="outline"
        size="sm"
        onClick={pollNow}
        disabled={busy}
        className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-xs"
        data-testid="gcal-sync-now-btn"
      >
        <RefreshCw className="w-3.5 h-3.5 mr-1" />
        Sync Now
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={disconnect}
        disabled={busy}
        className="text-zinc-500 hover:text-red-400 text-xs"
        data-testid="gcal-disconnect-btn"
      >
        <Unlink className="w-3.5 h-3.5 mr-1" />
        Disconnect
      </Button>
    </div>
  );
}
